import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { api } from '@/lib/api/client';
import { formatBillingPeriodDate } from '@/lib/utils/billingPeriod';
import type { ReportTask } from '../types';
import { useBillingPeriods, useReportTotals } from '../hooks';
import { useContractData } from './useContractData';
import { ReportSummaryTable } from './ReportSummaryTable';
import { ReportDataTable } from './ReportDataTable';

interface Filters {
  'endedAt.min'?: string;
  'endedAt.max'?: string;
  contractId?: string;
  billingPeriodId?: string;
  matterId?: string;
}

function formatTime(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return `${hours}h ${minutes}m`;
}

export function ReportClientFormatPage() {
  const { t } = useTranslation();
  const { contractId } = useParams<{ contractId: string }>();
  const [searchParams] = useSearchParams();
  const clientName = searchParams.get('clientName') || 'client';

  const [filters, setFilters] = useState<Filters>({
    'endedAt.min': undefined,
    'endedAt.max': undefined,
    contractId: undefined,
  });

  const updateFilter = (key: keyof Filters, value: string | undefined) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const updateFilterRef = useRef(updateFilter);

  // `endedAt.min`/`endedAt.max` are what the Reports page now sends; `minDate`/
  // `maxDate` are the legacy names, kept so older bookmarked links keep working.
  const qpMin = searchParams.get('endedAt.min') || searchParams.get('minDate') || undefined;
  const qpMax = searchParams.get('endedAt.max') || searchParams.get('maxDate') || undefined;
  const qpBillingPeriodId = searchParams.get('billingPeriodId') || undefined;
  const qpMatterId = searchParams.get('matterId') || undefined;

  const lastAppliedRef = useRef({
    min: undefined as string | undefined,
    max: undefined as string | undefined,
  });

  useEffect(() => {
    const toISO = (d: string | undefined, endOfDay = false): string | undefined => {
      if (!d || d === 'undefined') return undefined;
      if (/\dT/.test(d)) {
        const dt = new Date(d);
        return isNaN(dt.getTime()) ? undefined : dt.toISOString();
      }
      const suffix = endOfDay ? 'T23:59:59.999+05:00' : 'T00:00:00.000+05:00';
      const dt = new Date(`${d}${suffix}`);
      return isNaN(dt.getTime()) ? undefined : dt.toISOString();
    };

    const minISO = toISO(qpMin, false);
    const maxISO = toISO(qpMax, true);

    if (lastAppliedRef.current.min !== minISO) {
      updateFilterRef.current('endedAt.min', minISO);
      lastAppliedRef.current.min = minISO;
    }
    if (lastAppliedRef.current.max !== maxISO) {
      updateFilterRef.current('endedAt.max', maxISO);
      lastAppliedRef.current.max = maxISO;
    }
  }, [qpMin, qpMax]);

  useEffect(() => {
    updateFilterRef.current('contractId', contractId ?? undefined);
  }, [contractId]);

  useEffect(() => {
    updateFilterRef.current('billingPeriodId', qpBillingPeriodId);
  }, [qpBillingPeriodId]);

  useEffect(() => {
    updateFilterRef.current('matterId', qpMatterId);
  }, [qpMatterId]);

  const { selectedContract, hourlyPackage } = useContractData(contractId);

  const numericContractId = contractId ? Number(contractId) : undefined;
  const { data: billingPeriods } = useBillingPeriods(
    numericContractId,
    !!qpBillingPeriodId,
  );
  const selectedPeriod = useMemo(
    () =>
      qpBillingPeriodId
        ? (billingPeriods ?? []).find((p) => p?.id === Number(qpBillingPeriodId))
        : undefined,
    [billingPeriods, qpBillingPeriodId],
  );

  const { data } = useQuery({
    queryKey: ['getReportsClientFormat', filters],
    queryFn: async () => {
      const params: Record<string, unknown> = {};

      if (filters.contractId) {
        params.contractId = filters.contractId;
      }
      // Without these the endpoint's optional filters simply went unset and it
      // returned the contract's whole history — silently, with no error.
      // Defect AL-2026/08-03-01, D-04.
      if (filters.billingPeriodId) {
        params.billingPeriodId = filters.billingPeriodId;
      }
      if (filters.matterId) {
        params.matterId = filters.matterId;
      }
      if (filters['endedAt.min']) {
        const minDate = new Date(filters['endedAt.min']);
        minDate.setHours(0, 0, 0, 0);
        params['endedAt.min'] = minDate.toISOString();
      }
      if (filters['endedAt.max']) {
        const maxDate = new Date(filters['endedAt.max']);
        maxDate.setHours(23, 59, 59, 999);
        params['endedAt.max'] = maxDate.toISOString();
      }

      return api.get<ReportTask[]>('/reports/contracts/client', { params });
    },
    enabled: !!contractId,
  });

  // Identical calculation to ReportsPage — one hook, so the client-facing
  // document and the internal screen can never print different figures for the
  // same contract. Defect AL-2026/08-03-01, D-04/D-06.
  const totals = useReportTotals({
    tasks: data,
    contract: selectedContract,
    hourlyPackage,
    contractId: numericContractId,
    billingPeriodId: qpBillingPeriodId ? Number(qpBillingPeriodId) : undefined,
    min: filters['endedAt.min'],
    max: filters['endedAt.max'],
  });
  const totalDurationSeconds = totals.totalDuration;

  // Header dates: an explicit range wins, otherwise the selected billing period's
  // own boundaries. Only a genuinely unfiltered document says "whole period" —
  // it used to say that even when the user had picked one month.
  const minLabel =
    qpMin && qpMin !== 'undefined'
      ? new Date(qpMin).toLocaleDateString()
      : selectedPeriod
        ? formatBillingPeriodDate(selectedPeriod.startDate, true)
        : t('allPeriod');
  const maxLabel =
    qpMax && qpMax !== 'undefined'
      ? new Date(qpMax).toLocaleDateString()
      : selectedPeriod
        ? formatBillingPeriodDate(selectedPeriod.endDate)
        : t('allPeriod');

  const overTime = totals.overtimeSeconds ? formatTime(totals.overtimeSeconds) : '-';
  // Billed price column is always visible — prices on ACTIVE-package tasks may
  // still change as more activity lands, but the user wants the column shown
  // for every contract regardless of package state.
  const showBilledPrice = true;

  return (
    <div className="a4-container">
      <div className="flex py-2 px-4 justify-between items-center border-b header-bar">
        <h1 className="text-xl font-semibold">{t('reports')}</h1>
      </div>

      <div className="px-2">
        <div className="min-w-full table-auto">
          <div className="flex flex-col-reverse sm:flex-row items-center gap-2 py-4" />

          {selectedContract ? (
            <>
              <ReportSummaryTable
                selectedContract={selectedContract}
                clientName={clientName}
                minLabel={minLabel}
                maxLabel={maxLabel}
                totalBillableSeconds={totals.displayedBilled}
                totalBilledPrice={totals.displayedBilledPrice}
                overTime={overTime}
                showBilledPrice={showBilledPrice}
              />
              <ReportDataTable
                data={data}
                selectedContract={selectedContract}
                totalDurationSeconds={totalDurationSeconds}
                showBilledPrice={showBilledPrice}
              />
            </>
          ) : (
            <h1>{t('noDataFound')}</h1>
          )}
        </div>
      </div>
    </div>
  );
}
